/**
 * Session storage utilities for chat session persistence.
 *
 * Stores only a non-sensitive UUID in sessionStorage — the actual chat thread
 * lives server-side in DynamoDB. sessionStorage is tab-scoped: closing the
 * browser tab naturally ends the session.
 *
 * All sessionStorage calls are wrapped in try/catch: storage can be blocked
 * in private browsing, sandboxed iframes, or when the quota is exceeded.
 * When storage is unavailable the session still works for the current page
 * load (UUID lives in React state) but won't survive a refresh.
 */

const SESSION_KEY = 'porter_session_id';

function storageGet(): string | null {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

export function storageSet(id: string): void {
  try {
    sessionStorage.setItem(SESSION_KEY, id);
  } catch {
    // Storage blocked (private browsing, quota, sandboxed iframe) — the UUID
    // lives in React state for this page load but won't survive a refresh.
  }
}

function storageRemove(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Best-effort.
  }
}

/**
 * Returns true if a session ID already exists in sessionStorage (i.e. this is
 * a reload of an existing tab, not a new tab). Does not create a new ID.
 *
 * Use this to skip the GET /session fetch when there is nothing to restore.
 */
export function hasExistingSession(): boolean {
  return storageGet() !== null;
}

/**
 * Returns the existing session ID from sessionStorage, or generates and stores
 * a new UUID v4 if none exists.
 *
 * Safe to call on every render — returns the same value until
 * {@link clearSessionId} is called.
 */
export function getOrCreateSessionId(): string {
  const existing = storageGet();
  if (existing) return existing;
  const id = crypto.randomUUID();
  storageSet(id);
  return id;
}

/**
 * Removes the session ID from sessionStorage.
 *
 * Called on "New chat" and "Clear context" before generating a fresh ID.
 * After this call, {@link getOrCreateSessionId} will create a new UUID.
 */
export function clearSessionId(): void {
  storageRemove();
}
